import {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  type ChangeEvent,
  type FocusEvent,
  type ComponentProps,
} from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

import { cn, parseNumberOnly } from '../lib/utils';

const BASE_CLASS =
  'flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

interface InputProps extends ComponentProps<'input'> {
  hideArrows?: boolean;
  /** Skip parseNumberOnly formatting during typing — validation still runs on blur */
  skipFormat?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      value,
      min,
      max,
      step = 1,
      onChange,
      onFocus,
      onBlur,
      disabled,
      hideArrows,
      skipFormat,
      ...props
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);
    const [displayValue, setDisplayValue] = useState(String(value ?? ''));
    const minNum = min != null ? Number(min) : undefined;
    const maxNum = max != null ? Number(max) : undefined;
    const stepNum = Number(step) || 1;
    const isNumber = type === 'number';
    const showArrows = isNumber && !hideArrows;

    // Sync display from parent value
    useEffect(() => {
      if (isNumber) setDisplayValue(String(value ?? ''));
    }, [value, isNumber]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      switch (type) {
        case 'number': {
          if (skipFormat) {
            setDisplayValue(e.target.value);
            break;
          }
          const raw = e.target.value;
          if (raw === '') {
            setDisplayValue('');
            return;
          }
          const parsed = parseNumberOnly(raw, minNum, maxNum, true, 2, false);
          const clamped = String(parsed);
          setDisplayValue(clamped);
          e.target.value = clamped;
          break;
        }
      }
      onChange?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      switch (type) {
        case 'number': {
          if (displayValue === '') {
            const restored = String(value ?? minNum ?? 0);
            setDisplayValue(restored);
            e.target.value = restored;
          } else {
            const clamped = String(parseNumberOnly(displayValue, minNum, maxNum, true, 2, true));
            if (clamped !== displayValue) {
              setDisplayValue(clamped);
              const nativeSet = Object.getOwnPropertyDescriptor(
                HTMLInputElement.prototype,
                'value',
              )?.set;
              nativeSet?.call(innerRef.current, clamped);
              innerRef.current?.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
          break;
        }
      }
      onBlur?.(e);
    };

    const handleStep = (direction: 1 | -1) => {
      const el = innerRef.current;
      if (!el) return;
      const current = parseFloat(el.value) || 0;
      const next = parseNumberOnly(
        String(current + direction * stepNum),
        minNum,
        maxNum,
        true,
        2,
        true,
      );
      const finalValue = typeof next === 'number' ? next : current;
      const nativeSet = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
      nativeSet?.call(el, String(finalValue));
      el.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const inputEl = (
      <input
        type={type}
        className={cn(
          BASE_CLASS,
          isNumber &&
            '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]',
          showArrows && 'pr-6',
          !showArrows && className,
        )}
        ref={innerRef}
        min={min}
        max={max}
        step={step}
        value={isNumber ? displayValue : value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={handleBlur}
        disabled={disabled}
        {...props}
      />
    );

    if (!showArrows) return inputEl;

    const currentNum = parseFloat(displayValue) || 0;
    const atMax = maxNum != null && currentNum >= maxNum;
    const atMin = minNum != null && currentNum <= minNum;

    return (
      <div className={cn('relative flex w-full', className)}>
        {inputEl}
        <div className="absolute right-0 inset-y-0 flex flex-col border-l border-input">
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled || atMax}
            className="flex flex-1 cursor-pointer items-center justify-center px-1 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleStep(1)}
            aria-label="Increase"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled || atMin}
            className="flex flex-1 cursor-pointer items-center justify-center px-1 border-t border-input text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleStep(-1)}
            aria-label="Decrease"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input, type InputProps };
